import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Gavel, Plus, Search, X } from 'lucide-react';
import { usePracticeStore } from '../store/practiceStore';
import { CaseListItem } from '../types';
import { CaseForm } from '../components/practice/CaseForm';
import { dayLabel } from '../components/practice/format';
import { Badge, Button, EmptyState } from '../components/ui';

function statusBadge(row: CaseListItem) {
  if (!row.case_status) return <Badge tone="neutral">File</Badge>;
  if (row.case_status === 'active') return <Badge tone="success">Active</Badge>;
  if (row.case_status === 'disposed') return <Badge tone="info">Disposed</Badge>;
  return <Badge tone="neutral">Archived</Badge>;
}

type CaseFilter = 'active' | 'court' | 'files' | 'archived' | 'disposed';

const FILTERS: { value: CaseFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'court', label: 'Court cases' },
  { value: 'files', label: 'Files only' },
  { value: 'archived', label: 'Archived' },
  { value: 'disposed', label: 'Disposed' },
];

/** case_status semantics: null = plain case file (no court tracking). */
function matchesFilter(row: CaseListItem, filter: CaseFilter): boolean {
  switch (filter) {
    case 'active':
      // Everything you're working on: live court cases + plain files.
      return row.case_status === 'active' || !row.case_status;
    case 'court':
      return row.case_status === 'active';
    case 'files':
      return !row.case_status;
    case 'archived':
      return row.case_status === 'archived';
    case 'disposed':
      return row.case_status === 'disposed';
  }
}

/** One merged list — court cases and plain case files together (absorbs Matters). */
export function CasesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cases, casesFetchedAt, isFetchingCases, fetchCases } = usePracticeStore();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<CaseFilter>('active');
  // The ＋ New menu deep-links here with a create intent (state.create).
  const [showForm, setShowForm] = useState(
    Boolean((location.state as { create?: boolean } | null)?.create)
  );

  // The merged list always needs every matter — filtering is client-side.
  useEffect(() => {
    fetchCases('all');
  }, [fetchCases]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return cases.filter((c) => {
      if (!matchesFilter(c, filter)) return false;
      if (!q) return true;
      return [c.title, c.client_name, c.case_number]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [cases, query, filter]);

  const isFirstLoad = cases.length === 0 && (isFetchingCases || casesFetchedAt === null);

  return (
    <div className="p-2 sm:p-6 max-w-3xl mx-auto">
      {/* data-tour: stable anchors for the walkthrough — exist with zero cases */}
      <div data-tour="cases-list" className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-display text-3xl font-medium text-legal-900">Cases</h1>
          <p className="text-legal-500 mt-1 text-[15px]">
            Every case with its next date — tap one to open its case file.
          </p>
        </div>
        <Button
          size="md"
          data-track="new-case"
          data-tour="new-case"
          onClick={() => setShowForm((v) => !v)}
          className="shrink-0"
        >
          {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          <span className="hidden sm:inline">{showForm ? 'Cancel' : 'New case'}</span>
          <span className="sm:hidden">{showForm ? 'Cancel' : 'New'}</span>
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-card border border-legal-200 p-4 mb-6 animate-slide-up">
          <CaseForm
            onSaved={(c) => {
              setShowForm(false);
              fetchCases('all');
              navigate(`/app/cases/${c.id}`, { state: { matter: c } });
            }}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {/* Filter chips + search */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mb-1">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              data-track={`cases-filter-${f.value}`}
              onClick={() => setFilter(f.value)}
              className={`shrink-0 px-3.5 py-2 rounded-full text-sm font-medium transition-colors press ${
                filter === f.value
                  ? 'bg-accent text-legal-900'
                  : 'bg-white border border-legal-200 text-legal-600 hover:border-legal-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-legal-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title, client, number…"
            className="w-full pl-9 pr-3 py-2.5 text-sm border border-legal-200 rounded-lg bg-white text-legal-800 placeholder-legal-400 focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
      </div>

      {isFirstLoad ? (
        <div className="animate-pulse space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 bg-legal-100 rounded-card" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <EmptyState
          icon={Gavel}
          title={query ? 'No matches' : filter === 'active' ? 'No cases yet' : 'Nothing here'}
          message={
            query
              ? 'Nothing matches that search.'
              : filter === 'active'
              ? 'Add your first case — hearings, research, drafts and documents live in one file.'
              : 'No cases under this filter.'
          }
          action={
            !query &&
            filter === 'active' && (
              <Button size="md" data-track="new-case" onClick={() => setShowForm(true)}>
                <Plus className="h-4 w-4" /> New case
              </Button>
            )
          }
        />
      ) : (
        <div className="space-y-3 stagger-children">
          {shown.map((row) => (
            <Link
              key={row.id}
              to={`/app/cases/${row.id}`}
              state={{ matter: row }}
              className="block bg-white rounded-card border border-legal-200 p-4 hover:border-legal-300 card-lift"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-legal-900 break-words">{row.title}</p>
                  <p className="text-xs text-legal-400 mt-0.5 break-words">
                    {[row.client_name, row.court_name, row.case_number]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </p>
                </div>
                {row.next_hearing_date && (
                  <div className="text-right shrink-0">
                    <p className="text-sm font-medium text-legal-800">
                      {dayLabel(row.next_hearing_date)}
                    </p>
                    {row.next_hearing_purpose && (
                      <p className="text-[11px] text-legal-400 max-w-[8rem] truncate">
                        {row.next_hearing_purpose}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                {statusBadge(row)}
                {row.case_stage && <Badge tone="info">{row.case_stage}</Badge>}
                {row.shared_by && <Badge tone="accent">Shared by {row.shared_by}</Badge>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
